import { Badge } from "@repo/ui/components/ui/badge";
import { cn } from "@repo/ui/lib/utils";
import { CircleDashed } from "lucide-react";
import * as React from "react";
import { formatMeasured } from "@/shared/lib/format-measured";
import type { AssessmentScoreBand, AssessmentScoreRow } from "../lib/assessment-detail-model";
import { LABEL_CELL, PANE_GRID, VALUE_CELL } from "./assessment-detail-body";

export function AssessmentScores({ rows }: { rows: readonly AssessmentScoreRow[] }) {
  if (rows.length === 0) return null;

  return (
    <section aria-label="Scores" className="mb-6 max-w-3xl border-b pb-5">
      <h3 className="mb-2 text-sm font-semibold">Scores</h3>
      <div className={PANE_GRID}>
        {rows.map((row) => (
          <div key={row.scoreId} className="contents">
            <div className={`${LABEL_CELL} text-sm text-muted-foreground`}>{row.label}</div>
            <div className={VALUE_CELL}>
              <ScoreValue row={row} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Where the bar's lightest step starts: the lowest mix of the primary that still clears 2:1 on both surfaces. */
const LIGHTEST_STEP = 45;

function bandColor(index: number, count: number): string {
  const step = count > 1 ? LIGHTEST_STEP + ((100 - LIGHTEST_STEP) * index) / (count - 1) : 100;
  return `color-mix(in oklab, var(--primary) ${step}%, var(--background))`;
}

/**
 * The score as a marker on its scale, in three rows: the bar, the scale's
 * numbers (minimum, each split between bands, maximum), then each band's name
 * centred under its stretch. Bands are steps of the primary from light to dark,
 * each as wide as its share of the scale; an unbanded score is one bar in the
 * primary.
 */
function ScoreBar({
  label,
  value,
  minimum = 0,
  maximum,
  bands,
  note,
}: {
  label: string;
  minimum?: number;
  /** Absent until the score is recorded: the scale shows, with no marker on it. */
  value?: number;
  maximum: number;
  bands?: AssessmentScoreBand[];
  /** Shown where the value would sit, while there is none. */
  note?: React.ReactNode;
}) {
  const start = Math.min(minimum, bands?.[0]?.from ?? minimum);
  const span = Math.max(maximum - start, 1);
  const at = (point: number) => `${Math.round((Math.min(Math.max(point - start, 0), span) / span) * 100)}%`;
  const marker = value === undefined ? undefined : at(value);
  const scaleRef = React.useRef<HTMLDivElement>(null);
  const [scaleWidth, setScaleWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const scale = scaleRef.current;
    if (!scale) return;
    const observer = new ResizeObserver(() => setScaleWidth(scale.clientWidth));
    setScaleWidth(scale.clientWidth);
    observer.observe(scale);
    return () => observer.disconnect();
  }, []);
  // Inclusive integer slots keep the maximum visible, including when it is unbanded.
  const bandSpan = Math.max(maximum - start + 1, 1);
  const bandStyle = (band: AssessmentScoreBand) => ({
    left: `${((Math.max(band.from, start) - start) / bandSpan) * 100}%`,
    width: `${(Math.max(1, Math.min(band.to, maximum) - Math.max(band.from, start) + 1) / bandSpan) * 100}%`,
  });
  const ticks = [
    ...new Set([
      start,
      ...(bands?.flatMap((band, index) => [
        band.from,
        ...(band.to < (bands[index + 1]?.from ?? maximum + 1) - 1 ? [band.to] : []),
      ]) ?? []),
      maximum,
    ]),
  ]
    .filter((point) => point >= start && point <= maximum)
    .sort((a, b) => a - b);

  // Keep ticks anchored to their score; move crowded labels onto another row.
  const rowEnds: number[] = [];
  const tickRows = ticks.map((point) => {
    const width = String(point).length * 8 + 8;
    const position = (Number.parseFloat(at(point)) / 100) * scaleWidth;
    const left = point === start ? 0 : point === maximum ? position - width : position - width / 2;
    let row = rowEnds.findIndex((end) => end <= left);
    if (row === -1) row = rowEnds.length;
    rowEnds[row] = left + width;
    return row;
  });

  return (
    <div data-score-bar className="w-full max-w-xl space-y-1">
      {value !== undefined && (
        <span className="sr-only">
          {[`${value} of ${maximum}`, bands?.find((band) => band.current)?.label].filter(Boolean).join(", ")}
        </span>
      )}
      <div className="relative h-4">
        {!marker && note && (
          <span className="absolute bottom-0 left-0 inline-flex items-center gap-1.5 text-xs leading-none text-muted-foreground">
            {note}
          </span>
        )}
        {marker && (
          <span
            data-marker-value
            aria-hidden
            className="absolute bottom-0 -translate-x-1/2 text-base font-bold leading-none tabular-nums"
            style={{ left: marker }}
          >
            {value}
          </span>
        )}
      </div>
      <div className="relative py-1">
        <div
          data-track
          className={cn("relative h-2 overflow-hidden rounded-full bg-muted", !bands && "bg-primary")}
          aria-hidden
        >
          {bands?.map((band, index) => (
            <div
              key={band.label}
              data-band
              className="absolute h-full"
              style={
                {
                  ...bandStyle(band),
                  "--band-color": bandColor(index, bands.length),
                  backgroundColor: "var(--band-color)",
                } as React.CSSProperties
              }
            />
          ))}
        </div>
        {marker && (
          <div
            data-marker
            aria-hidden
            className="absolute top-1/2 h-4 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground"
            style={{ left: marker }}
          />
        )}
      </div>
      <div
        ref={scaleRef}
        data-scale
        className="relative text-xs leading-none tabular-nums text-muted-foreground"
        style={{ height: `${rowEnds.length}rem` }}
        aria-hidden
      >
        {ticks.map((point, index) => (
          <span
            key={point}
            data-scale-min={point === start ? "" : undefined}
            data-scale-max={point === maximum ? "" : undefined}
            data-boundary={point !== start && point !== maximum ? "" : undefined}
            className="absolute"
            style={{
              left: at(point),
              top: `${tickRows[index]}rem`,
              transform: `translateX(${point === start ? 0 : point === maximum ? -100 : -50}%)`,
            }}
          >
            {point}
          </span>
        ))}
      </div>
      {bands && (
        <ul
          aria-label={`${label} bands`}
          className="grid min-h-8"
          style={{ gridTemplateColumns: `repeat(${bandSpan}, minmax(0, 1fr))` }}
        >
          {bands.map((band) => (
            <li
              key={band.label}
              aria-current={band.current ? "true" : undefined}
              className={cn(
                "min-w-0 overflow-hidden wrap-anywhere text-center text-xs leading-tight",
                band.current ? "font-medium text-foreground" : "text-muted-foreground",
              )}
              style={{
                gridColumn: `${Math.max(band.from, start) - start + 1} / span ${Math.max(1, Math.min(band.to, maximum) - Math.max(band.from, start) + 1)}`,
              }}
            >
              {band.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PENDING_NOTES: Record<NonNullable<AssessmentScoreRow["pending"]>, string> = {
  scoring: "Scoring…",
  unscored: "Not scored yet",
  retracted: "Retracted",
};

/** A measured score has no scale to mark, so it reads as a value in its unit. */
function ScoreValue({ row }: { row: AssessmentScoreRow }) {
  const { scale } = row;
  if (!row.result) {
    const note = (
      <>
        {row.pending === "scoring" && <CircleDashed className="size-3.5" />}
        {PENDING_NOTES[row.pending ?? "scoring"]}
      </>
    );
    return scale.kind === "bounded" && scale.maximum > 0 ? (
      <ScoreBar label={row.label} minimum={scale.minimum} maximum={scale.maximum} bands={scale.bands} note={note} />
    ) : (
      <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">{note}</span>
    );
  }
  if (scale.kind === "measured")
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="font-medium tabular-nums">{formatMeasured(row.result.value, scale.unit)}</span>
        {row.history?.length ? (
          <span className="text-muted-foreground tabular-nums">
            Previously {row.history.map((value) => formatMeasured(value, scale.unit)).join(", ")}
          </span>
        ) : null}
        {row.result.amended && (
          <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
            amended
          </Badge>
        )}
      </div>
    );
  return (
    <div className="flex items-center gap-3">
      {scale.maximum > 0 ? (
        <ScoreBar
          label={row.label}
          value={row.result.value}
          minimum={scale.minimum}
          maximum={scale.maximum}
          bands={scale.bands}
        />
      ) : (
        // No points means a maximum of zero: no scale to put a marker on, so the value stands alone.
        <span className="text-sm font-medium tabular-nums">{row.result.value}</span>
      )}
      {row.result.band && !scale.bands && <Badge variant="secondary">{row.result.band}</Badge>}
      {row.result.amended && (
        <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
          amended
        </Badge>
      )}
    </div>
  );
}
